import * as React from "react";
import CappedTransferRow, { CappedTransferRowData } from "./CappedTransferRow";
import FlexiTransferRow, { FlexiTransferRowData } from "./FlexiTransferRow";
import { DrawdownType } from "../../../../../../../Wrap/Constants/Enums/Drawdown/DrawdownType";
import { PensionContribution } from "../../../../../../../Wrap/Models/Models/Processes/Apply/Pension/PensionContribution";
import {
    editIncomeDrawdownDetailsLabels,
    editIncomeDrawdownDetailsMessages,
    TransferOrDrawdown,
} from "../../Resource/Contents";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { PensionArrangementType } from "../../../../../../../Wrap/Constants/Enums/Domain/PensionArrangementType";
import { calculateNetDrawdownValue } from "../../../Common/SIPP/DrawdownDetails/Helper/DrawdownDetailHelper";
import { MoneyHelper } from "../../../../../../Shared/Common/MoneyHelper";

interface IIncomeFromPensionProps extends IProcessComponentProps {
    data: PensionContribution;
    onChange: (changes) => void;
    isITFC: boolean;
}
interface IIncomeFromPensionState {}

export class IncomeFromPension extends ProcessComponent<IIncomeFromPensionProps, IIncomeFromPensionState> {
    constructor(props: any) {
        super(props);
    }

    private isShowIncomeFromPension(data: PensionContribution) {
        return (
            data?.DrawdownModel?.Income?.TargetSpecificArrangements ||
            (data?.PensionTransfers?.length > 0 &&
                data?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome
                    ?.TargetSpecificArrangements)
        );
    }

    private extractCappedTransfers(): TransferOrDrawdown[] {
        const { data } = this.props;
        const cappedTransfers = [];
        if (data?.DrawdownModel?.DrawdownType === DrawdownType.Capped) {
            const drawdownModel = data.DrawdownModel;
            const drawdownTransfer: TransferOrDrawdown = {
                IsInternal: true,
                ProviderDetails: {
                    Name: editIncomeDrawdownDetailsLabels.crystallisation,
                },
                CurrentValue: {
                    Amount: calculateNetDrawdownValue(drawdownModel, data?.PensionProtection),
                    Currency: CurrencyCode.GBP,
                },
                IsInheritedFund: false,
                IncomePaymentDetails: {
                    // @TODO uncomment after SLP-69087 promoted

                    GadMaximum: {
                        Amount: drawdownModel.GadRates?.GadMaximum,
                        Currency: CurrencyCode.GBP,
                    },
                    // NextGadMaximum: {
                    //     Amount: drawdownModel.GadRates?.NextGadMaximum,
                    //     Currency: CurrencyCode.GBP
                    // },
                    // LastGadReviewDate: drawdownModel.LastGadReviewDate,
                    CurrentYearIncome: drawdownModel.Income.CurrentYearIncome,
                    GrossIncomePerPayment: drawdownModel.Income.GrossIncomePerPayment,
                    FutureYearIncome: drawdownModel.Income.FutureYearIncome,
                    // MaximumIncomePaymentForFutureYears: drawdownModel.MaximumIncomePaymentForFutureYears,
                    // MaximumIncomePaymentForRemainderOfYear: drawdownModel.MaximumIncomePaymentForRemainderOfYear,
                },
            };
            cappedTransfers.push(drawdownTransfer);
        }

        data?.PensionTransfers?.forEach((transfer, index) => {
            const trans = { PensionTransfersIndex: null, ...transfer };
            if (transfer.PensionSource === PensionArrangementType.Crystallised) {
                if (trans.DrawdownType == DrawdownType.Capped) {
                    trans.PensionTransfersIndex = index;
                    trans.IsInheritedFund = trans?.PensionDetails?.IsDependentsPension == true
                    cappedTransfers.push(trans);
                }
            }
        });

        return cappedTransfers;
    }

    private extractFlexiTransfers(): TransferOrDrawdown[] {
        const flexiTransfers = [];
        const { data } = this.props;
        if (data?.DrawdownModel?.DrawdownType === DrawdownType.Flexible) {
            const drawdownModel = data.DrawdownModel;

            const drawdownTransfer: TransferOrDrawdown = {
                IsInternal: true,
                ProviderDetails: {
                    Name: editIncomeDrawdownDetailsLabels.crystallisation,
                },
                CurrentValue: {
                    Amount: calculateNetDrawdownValue(drawdownModel, data?.PensionProtection),
                    Currency: CurrencyCode.GBP,
                },
                IsInheritedFund: false,
                IncomePaymentDetails: {
                    // @TODO uncomment after SLP-69087 MaximumIncomePerPayment: drawdownModel.MaximumIncomePerPayment,
                    GrossIncomePerPayment: drawdownModel.Income?.GrossIncomePerPayment,
                },
            };
            flexiTransfers.push(drawdownTransfer);
        }
        data?.PensionTransfers?.forEach((transfer, index) => {
            const trans = { PensionTransfersIndex: null, ...transfer };
            if (transfer.PensionSource === PensionArrangementType.Crystallised) {
                if (trans.DrawdownType == DrawdownType.Flexible) {
                    trans.PensionTransfersIndex = index;
                    trans.IsInheritedFund = trans?.PensionDetails?.IsDependentsPension == true
                    flexiTransfers.push(trans);
                }
            }
        });
        return flexiTransfers;
    }

    public render(): JSX.Element {
        const { data, onChange, isITFC } = this.props;

        const cappedTransfers = this.extractCappedTransfers();
        const flexiTransfers = this.extractFlexiTransfers();
        const cappedTransfersInTwos = cappedTransfers.reduce(
            (accumulator: Array<CappedTransferRowData>, transfer: TransferOrDrawdown, index: number) => {
                if (index % 2 === 0) {
                    // index is even
                    accumulator.push({
                        CappedTransfer1: transfer,
                    });
                } else {
                    accumulator[accumulator.length - 1].CappedTransfer2 = transfer;
                }
                return accumulator;
            },
            []
        );
        const flexiTransfersInTwos = flexiTransfers.reduce(
            (accumulator: Array<FlexiTransferRowData>, transfer: TransferOrDrawdown, index: number) => {
                if (index % 2 === 0) {
                    // index is even
                    accumulator.push({
                        FlexiTransfer1: transfer,
                    });
                } else {
                    accumulator[accumulator.length - 1].FlexiTransfer2 = transfer;
                }
                return accumulator;
            },
            []
        );

        return (
            <>
                {this.isShowIncomeFromPension(data) &&
                    cappedTransfersInTwos.map((transfers: CappedTransferRowData, key: number) => {
                        return (
                            <CappedTransferRow
                                key={key}
                                data={transfers}
                                onChange={(transferChanges, transfer) => {
                                    if (transfer.IsInternal) {
                                        const incomeChanges = { ...data.DrawdownModel.Income, ...transferChanges };
                                        let changes = { ...data.DrawdownModel, ...{ Income: incomeChanges } };
                                        this.onDataChange(onChange, changes, data, "DrawdownModel");
                                    } else {
                                        const pensionTransfers = data.PensionTransfers;
                                        const transferIndex = transfer.PensionTransfersIndex;
                                        const incomePaymentDetails =
                                            pensionTransfers[transferIndex].IncomePaymentDetails;
                                        pensionTransfers[transferIndex].IncomePaymentDetails = {
                                            ...incomePaymentDetails,
                                            ...transferChanges,
                                        };
                                        this.onDataChange(onChange, pensionTransfers, data, "PensionApply");
                                    }
                                }}
                                onRef={(ref) => this.appendChild(ref)}
                                pensionBase={data}
                            />
                        );
                    })}
                {this.isShowIncomeFromPension(data) &&
                    flexiTransfersInTwos.map((transfer, key) => {
                        return (
                            <FlexiTransferRow
                                key={key}
                                data={transfer}
                                onChange={(transferChanges, transfer) => {
                                    if (transfer.IsInternal) {
                                        const incomeChanges = { ...data.DrawdownModel.Income, ...transferChanges };
                                        let changes = { ...data.DrawdownModel, ...{ Income: incomeChanges } };
                                        this.onDataChange(onChange, changes, data, "DrawdownModel");
                                    } else {
                                        const pensionTransfers = data.PensionTransfers;
                                        const transferIndex = transfer.PensionTransfersIndex;
                                        const incomePaymentDetails =
                                            pensionTransfers[transferIndex].IncomePaymentDetails;
                                        pensionTransfers[transferIndex].IncomePaymentDetails = {
                                            ...incomePaymentDetails,
                                            ...transferChanges,
                                        };
                                        this.onDataChange(onChange, pensionTransfers, data, "PensionApply");
                                    }
                                }}
                                onRef={(ref) => this.appendChild(ref)}
                                pensionBase={data}
                                isITFC={isITFC}
                            />
                        );
                    })}
                {/* <TransferTotals
                    cappedTransfers={cappedTransfers}
                    flexiTransfers={flexiTransfers}
                    data={data}
                    showErrorMsgPopUp={this.props.showErrorMsgPopUp}
                    onChange={(v) => {
                        let changes = { ...data.DrawdownModel, ...v };
                        this.onDataChange(onChange, changes, data, "DrawdownModel");
                    }}
                    onRef={(ref) => this.appendChild(ref)}
                /> */}
            </>
        );
    }
}

export default IncomeFromPension;
